import React, { useEffect, useMemo, useState } from 'react';
import {
  formatPaperRaw,
  formatTmuaGrade,
  loadTmuaPaperScores,
  loadTmuaTarget,
  resetTmuaSittingProgress,
  saveTmuaPaperScore,
  saveTmuaTarget,
  sittingScoreFromPapers,
} from '../utils/tmuaScore';
import {
  sittingStatusFromDbPapers,
} from '../utils/paperAttemptHelpers';
import useWorkspacePaperProgress, { useUserTarget } from '../hooks/useWorkspacePaperProgress';
import { collectTopicPaperIds, topicAttemptRowFromMap, topicAttemptStatusRequestKey, topicRowAction } from '../utils/topicAttemptRow';
import { firstPopulatedMockTier } from '../utils/firstPopulatedMockTier';
import { useAuth } from '../../auth/hooks/useAuth';
import PracticeTopicsNav from './PracticeTopicsNav';
import PracticeTopicsPages from './PracticeTopicsPages';
import { topicPapersContent } from './TopicPapersBlock';
import {
  SUBTOPIC_PAPERS_LABEL,
  SUBTOPIC_PAPERS_ALL_PAGE,
  SUBTOPIC_TIER_PAGES,
  SUBTOPIC_PAGE_TITLES,
  subtopicPapersOfTier,
} from '../utils/subtopicPapers';
import PracticeTopicsOverview from './PracticeTopicsOverview';
import useSectionPaperTierProgress from '../hooks/useSectionPaperTierProgress';
import { PRACTICE_TOPIC_PAGE_TITLES, practiceProgressSummary } from '../utils/practiceTopics';
import './TmuaWorkspace.css';

const PAGES = {
  overview: 'Overview',
  ...PRACTICE_TOPIC_PAGE_TITLES,
  'mock-all': 'Mock Tests',
  'mock-beginnings': 'Mock Tests: Beginnings',
  'mock-foundations': 'Mock Tests: Foundations',
  'mock-beyond': 'Mock Tests: Beyond',
  past: 'Past Papers',
};

const TARGET_OPTIONS = [
  { value: 6.5, label: 'University of Cambridge — Computer Science (BA)' },
  { value: 6.5, label: 'University of Oxford — Mathematics & CS' },
  { value: 6.5, label: 'Imperial College London — CS & Mathematics' },
  { value: 6.0, label: 'LSE — Economics / Data Science' },
  { value: 6.5, label: 'University of Warwick — Maths / CS' },
  { value: 6.0, label: 'Durham University — Maths & Data Science' },
  { value: 6.0, label: 'University of Bath — Maths & CS' },
  { value: 5.5, label: 'University of Sheffield — Computer Science' },
  { value: 5.5, label: 'University of Southampton — Maths & Software Eng' },
  { value: 5.5, label: 'Lancaster University — Maths & Data Analytics' },
];

function paperIds(sitting) {
  return [sitting?.paper1?.id, sitting?.paper2?.id].filter(Boolean);
}

function sittingLabel(sitting, index, kind) {
  if (kind === 'past') {
    const year = String(sitting?.title || '').match(/\b(20\d{2})\b/);
    return year ? year[1] : sitting?.title || `Paper ${index + 1}`;
  }
  return `Test ${index + 1}`;
}

function StatusPill({ status }) {
  const map = {
    done: ['completed', 'Completed'],
    progress: ['in-progress', 'In progress'],
    none: ['not-started', 'Not started'],
  };
  const [cls, label] = map[status] || map.none;
  return <span className={`tmua-ws-pill ${cls}`}>{label}</span>;
}

function ModeCell({ mode, status }) {
  if (status === 'none' || !mode) return '—';
  return <span className="tmua-ws-mode">{mode === 'timed' ? 'Timed' : 'Untimed'}</span>;
}

function avgGrades(grades) {
  const nums = grades.filter((g) => g != null && Number.isFinite(Number(g))).map(Number);
  if (!nums.length) return null;
  return Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10;
}

function enrichSittings(sittings, scoresMap, dbPapers = {}) {
  return (sittings || []).map((sitting, index) => {
    const p1Id = sitting.paper1?.id;
    const p2Id = sitting.paper2?.id;
    const scored = sittingScoreFromPapers(p1Id, p2Id, scoresMap);
    let status = sittingStatusFromDbPapers(p1Id, p2Id, dbPapers);
    if (status === 'none') {
      status = scored.status;
      if (status === 'none' && sitting.completed) status = 'done';
    }
    return { ...sitting, index, scored, status };
  });
}

function tierStats(rows) {
  const done = rows.filter((r) => r.status === 'done').length;
  const grades = rows.map((r) => r.scored.grade);
  return {
    done,
    total: rows.length,
    avg: avgGrades(grades),
    pct: rows.length ? Math.round((done / rows.length) * 100) : 0,
  };
}

const TmuaWorkspace = ({
  onBack,
  mockSittings = [],
  pastSittings = [],
  sectionPapers = [],
  subtopicPapers = [],
  topicSections = [],
  isPartUnlocked,
  onStartSetPaper,
  onStartTopicPaper,
  topicStatus = 'ready',
  onContinueSitting,
  onReviewSitting,
  onRequestUpgrade,
}) => {
  const { user } = useAuth();
  const userId = user?.id || null;
  const [page, setPage] = useState('overview');
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scoreTick, setScoreTick] = useState(0);
  const [toast, setToast] = useState(null);
  const [target, setTarget] = useUserTarget(loadTmuaTarget, saveTmuaTarget, userId);

  useEffect(() => {
    document.body.classList.add('exam-workspace-open');
    return () => document.body.classList.remove('exam-workspace-open');
  }, []);

  const statusRequestKey = topicAttemptStatusRequestKey([
    ...[...mockSittings, ...pastSittings].flatMap((s) => [s?.paper1?.id, s?.paper2?.id].filter(Boolean)),
    ...collectTopicPaperIds(topicSections),
  ]);

  const {
    dbPapers,
    scoresMap,
    completedList,
  } = useWorkspacePaperProgress('TMUA', statusRequestKey, {
    userId,
    loadScores: loadTmuaPaperScores,
    saveScore: saveTmuaPaperScore,
    cacheTick: scoreTick,
  });



  const beginnings = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.paper1?.tier || s.paper2?.tier) === 'beginnings'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );
  const foundations = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.paper1?.tier || s.paper2?.tier) === 'foundations'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );
  const beyond = useMemo(
    () => enrichSittings(
      mockSittings.filter((s) => (s.tier || s.paper1?.tier || s.paper2?.tier) === 'beyond'),
      scoresMap,
      dbPapers
    ),
    [mockSittings, scoresMap, dbPapers]
  );

  const mocksGoto = useMemo(
    () => firstPopulatedMockTier(beginnings, foundations, beyond),
    [beginnings, foundations, beyond]
  );
  const past = useMemo(
    () => enrichSittings(pastSittings, scoresMap, dbPapers),
    [pastSittings, scoresMap, dbPapers]
  );
  const topicCount = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce((n, t) => n + (t.params?.length || 0), 0),
      0
    ),
    [topicSections]
  );
  const topicsDone = useMemo(
    () => (topicSections || []).reduce(
      (sum, sec) => sum + (sec.tiers || []).reduce(
        (n, t) => n + (t.params || []).filter((p) => completedList.includes(p.paper_id)).length,
        0
      ),
      0
    ),
    [topicSections, completedList]
  );

  // Section papers are listed under Practice Topics (Foundations and Beyond), so the
  // Overview's Practice Topics rows count them with the topics.
  const sectionProgress = useSectionPaperTierProgress('TMUA', sectionPapers, completedList);
  const practiceTopicsRow = practiceProgressSummary({ topicsDone, topicCount, papers: sectionProgress.all });

  const overview = useMemo(() => {
    const allMocks = [...beginnings, ...foundations, ...beyond];
    const all = [...allMocks, ...past];
    const graded = all.filter((r) => r.scored.grade != null);
    const currentAvg = avgGrades(graded.map((r) => r.scored.grade));

    let totalQuestions = 0;
    let attempted = 0;
    let correct = 0;
    all.forEach((row) => {
      // Catalog size from paper list (DB quiz_papers.question_count / API) — not hardcoded.
      totalQuestions += row.questionCount || 0;
      [row.scored.p1, row.scored.p2].forEach((s) => {
        if (!s) return;
        // Count only questions actually answered (pause mid-paper must not use full paper size).
        const answered = Number(s.answered);
        attempted += Number.isFinite(answered) ? answered : (Number(s.total) || 0);
        correct += Number(s.correct) || 0;
      });
    });

    const timed = graded.filter((r) => r.scored.mode === 'timed');
    const untimed = graded.filter((r) => r.scored.mode === 'untimed');
    const mocksDone = allMocks.filter((r) => r.status === 'done').length;
    const pastDone = past.filter((r) => r.status === 'done').length;

    return {
      currentAvg,
      totalQuestions,
      attempted,
      successRate: attempted > 0 ? Math.round((correct / attempted) * 100) : null,
      scoredAttempts: graded.length,
      beginnings: tierStats(beginnings),
      foundations: tierStats(foundations),
      beyond: tierStats(beyond),
      mocks: {
        done: mocksDone,
        total: allMocks.length,
        avg: avgGrades(allMocks.map((r) => r.scored.grade)),
        pct: allMocks.length ? Math.round((mocksDone / allMocks.length) * 100) : 0,
      },
      past: {
        done: pastDone,
        total: past.length,
        avg: avgGrades(past.map((r) => r.scored.grade)),
        pct: past.length ? Math.round((pastDone / past.length) * 100) : 0,
      },
      timed: {
        count: timed.length,
        total: graded.length,
        avg: avgGrades(timed.map((r) => r.scored.grade)),
        pct: graded.length ? Math.round((timed.length / graded.length) * 100) : 0,
      },
      untimed: {
        count: untimed.length,
        total: graded.length,
        avg: avgGrades(untimed.map((r) => r.scored.grade)),
        pct: graded.length ? Math.round((untimed.length / graded.length) * 100) : 0,
      },
    };
  }, [beginnings, foundations, beyond, past]);

  const showToast = (msg) => {
    setToast(msg);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(''), 2600);
  };

  const goTo = (id) => {
    setPage(id);
    setMobileOpen(false);
  };

  const startSitting = (sitting, options = {}) => {
    const paper = sitting.paper1 || sitting.paper2;
    if (!paper) return;
    const unlocked = isPartUnlocked ? isPartUnlocked(paper) : true;
    if (!unlocked) {
      if (onRequestUpgrade) onRequestUpgrade(paper);
      return;
    }
    onStartSetPaper(
      'TMUA',
      sitting.startId,
      sitting.startCatalogId,
      sitting.paper2
        ? { id: sitting.paper2.id, catalog_id: sitting.paper2.catalog_id || sitting.paper2.id }
        : null,
      options
    );
  };

  const handleRetake = (sitting) => {
    resetTmuaSittingProgress(userId, paperIds(sitting));
    setScoreTick((n) => n + 1);
    startSitting(sitting, { forceNew: true });
  };

  const handleContinue = (sitting) => {
    if (typeof onContinueSitting === 'function' && sitting) {
      onContinueSitting(sitting);
      return;
    }
    showToast('Resume isn’t available yet — use Start or Retake for a new attempt.');
  };

  const handleReview = (sitting) => {
    if (typeof onReviewSitting === 'function' && sitting) {
      onReviewSitting(sitting);
      return;
    }
    showToast('Review isn’t available yet.');
  };

  const targetValue = Number(target?.value) || 0;
  const avg = overview.currentAvg;
  const fillPct = avg != null ? Math.min(100, (avg / 9) * 100) : 0;
  const markerPct = Math.min(100, (targetValue / 9) * 100);
  const diff = avg != null ? Math.round((avg - targetValue) * 10) / 10 : null;
  const markerLabel = (target?.label || 'Target').split(' — ')[0] || 'Target';

  const selectValue = target?.custom
    ? 'custom'
    : TARGET_OPTIONS.find((o) => o.label === target?.label)?.label || TARGET_OPTIONS[3].label;
  const selectedTrack = TARGET_OPTIONS.find((o) => o.label === selectValue);
  const selectDisplay = selectValue === 'custom'
    ? 'Custom target…'
    : selectedTrack
      ? `${selectedTrack.label} (${selectedTrack.value.toFixed(1)}+/9.0)`
      : selectValue;

  const onTargetChange = (e) => {
    const val = e.target.value;
    if (val === 'custom') {
      const next = { value: Number(target.value) || 6, label: 'Custom', custom: true };
      setTarget(next);
      return;
    }
    const opt = TARGET_OPTIONS.find((o) => o.label === val);
    if (!opt) return;
    const next = { value: opt.value, label: opt.label, custom: false };
    setTarget(next);
  };

  const onCustomInput = (e) => {
    const value = Number(e.target.value) || 0;
    const next = { value, label: 'Custom', custom: true };
    setTarget(next);
  };

  const renderTopicsTable = (params) => {
    if (!params?.length) return null;
    return (
      <table className="tmua-ws-table">
        <thead>
          <tr>
            <th>Topic</th>
            <th>Questions</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {params.map((param) => {
            const done = completedList.includes(param.paper_id);
            const action = topicRowAction(topicAttemptRowFromMap(dbPapers, param.paper_id), done);
            const status = action.status;
            const title = param.title || param.paper_id;
            return (
              <tr key={param.paper_id}>
                <td data-label="Topic" className="tmua-ws-cell-strong">{title}</td>
                <td data-label="Questions" className="tmua-ws-cell-mono">
                  {param.question_count != null ? param.question_count : '—'}
                </td>
                <td data-label="Status">
                  <StatusPill status={status} />
                </td>
                <td data-label="">
                  <button
                    type="button"
                    className={action.resume ? 'tmua-ws-start-btn mid' : 'tmua-ws-start-btn'}
                    onClick={() => onStartTopicPaper?.('TMUA', param, {
                      forceNew: action.forceNew,
                      resume: action.resume,
                    })}
                  >
                    {action.label}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  const renderTable = (rows, kind) => (
    <table className="tmua-ws-table">
      <thead>
        <tr>
          <th>{kind === 'past' ? 'Paper' : 'Test'}</th>
          <th>Paper 1</th>
          <th>Paper 2</th>
          <th>Score</th>
          <th>Mode</th>
          <th>Status</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => {
          const paper = row.paper1 || row.paper2;
          const unlocked = isPartUnlocked ? isPartUnlocked(paper || row) : true;
          const { scored, status } = row;
          return (
            <tr key={row.startId || row.title}>
              <td data-label={kind === 'past' ? 'Paper' : 'Test'} className="tmua-ws-cell-strong">
                {sittingLabel(row, row.index, kind)}
                {!unlocked && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--tmua-ink-faint)' }}>Locked</span>
                )}
              </td>
              <td data-label="Paper 1" className="tmua-ws-cell-mono">
                {formatPaperRaw(scored.p1?.correct, scored.p1?.total)}
              </td>
              <td data-label="Paper 2" className="tmua-ws-cell-mono">
                {formatPaperRaw(scored.p2?.correct, scored.p2?.total)}
              </td>
              <td data-label="Score" className="tmua-ws-cell-mono">
                {formatTmuaGrade(scored.grade)}
              </td>
              <td data-label="Mode">
                <ModeCell mode={scored.mode} status={status} />
              </td>
              <td data-label="Status">
                <StatusPill status={status} />
              </td>
              <td data-label="">
                {status === 'done' ? (
                  <div className="tmua-ws-actions">
                    <button type="button" className="tmua-ws-start-btn ghost" onClick={() => handleReview(row)}>
                      Review
                    </button>
                    <button
                      type="button"
                      className="tmua-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : status === 'progress' ? (
                  <div className="tmua-ws-actions">
                    <button type="button" className="tmua-ws-start-btn mid" onClick={() => handleContinue(row)}>
                      Continue
                    </button>
                    <button
                      type="button"
                      className="tmua-ws-start-btn ghost"
                      onClick={() => (unlocked ? handleRetake(row) : onRequestUpgrade?.(paper))}
                    >
                      Retake
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="tmua-ws-start-btn"
                    onClick={() => {
                      if (!unlocked) {
                        onRequestUpgrade?.(paper);
                        return;
                      }
                      startSitting(row);
                    }}
                  >
                    Start
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  const BreakdownRow = ({ name, meta, score, pct, goto, staticRow }) => {
    const inner = (
      <>
        <div className="tmua-ws-breakdown-name">{name}</div>
        <div className="tmua-ws-breakdown-bar-wrap">
          <div className="tmua-ws-breakdown-bar">
            <div className="tmua-ws-breakdown-bar-fill" style={{ width: `${pct || 0}%` }} />
          </div>
          <div className="tmua-ws-breakdown-meta">{meta}</div>
        </div>
        <div className="tmua-ws-breakdown-score">{score}</div>
        {!staticRow && <div className="tmua-ws-breakdown-arrow">›</div>}
      </>
    );
    if (staticRow) {
      return <div className="tmua-ws-breakdown-row static">{inner}</div>;
    }
    return (
      <button type="button" className="tmua-ws-breakdown-row" onClick={() => goTo(goto)}>
        {inner}
      </button>
    );
  };

  return (
    <div className="tmua-ws">
      <aside className={`tmua-ws-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="tmua-ws-sidebar-head">
          <span className="tmua-ws-name">TMUA Workspace</span>
          <button
            type="button"
            className="tmua-ws-icon-btn"
            title="Collapse sidebar"
            onClick={() => (mobileOpen ? setMobileOpen(false) : setCollapsed((c) => !c))}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
              <line x1="9" y1="4" x2="9" y2="20" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
        </div>

        <nav className="tmua-ws-nav">
          <button
            type="button"
            className={`tmua-ws-nav-item ${page === 'overview' ? 'active' : ''}`}
            onClick={() => goTo('overview')}
          >
            <svg className="tmua-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="tmua-ws-nav-label">Overview</span>
          </button>

          <PracticeTopicsNav prefix="tmua-ws" page={page} onGo={goTo} />
          <PracticeTopicsNav
            prefix="tmua-ws"
            page={page}
            onGo={goTo}
            label={SUBTOPIC_PAPERS_LABEL}
            pages={SUBTOPIC_TIER_PAGES}
          />

          <div className="tmua-ws-nav-group">
            <div className="tmua-ws-nav-item tmua-ws-nav-parent">
              <svg className="tmua-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <path d="M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
              <span className="tmua-ws-nav-label">Mock Tests</span>
            </div>
            <div className="tmua-ws-nav-sub">
              {[
                ['mock-beginnings', 'Beginnings', 1],
                ['mock-foundations', 'Foundations', 2],
                ['mock-beyond', 'Beyond', 3],
              ].map(([id, label, tier]) => (
                <button
                  key={id}
                  type="button"
                  className={`tmua-ws-nav-subitem ${page === id ? 'active' : ''}`}
                  onClick={() => goTo(id)}
                >
                  <svg className="tmua-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                    <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                    <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                    <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
                  </svg>
                  <span className="tmua-ws-nav-label">{label}</span>
                </button>
              ))}
            </div>
          </div>

          {[
            ['mock-beginnings', 'Beginnings', 1],
            ['mock-foundations', 'Foundations', 2],
            ['mock-beyond', 'Beyond', 3],
          ].map(([id, label, tier]) => (
            <button
              key={`c-${id}`}
              type="button"
              className={`tmua-ws-nav-item tmua-ws-collapsed-tier ${page === id ? 'active' : ''}`}
              title={label}
              onClick={() => goTo(id)}
            >
              <svg className="tmua-ws-nav-icon" viewBox="0 0 24 24" fill="none">
                <rect x="4" y="14" width="4" height="7" rx="1" fill="currentColor" opacity={tier >= 1 ? 1 : 0.35} />
                <rect x="10" y="10" width="4" height="11" rx="1" fill="currentColor" opacity={tier >= 2 ? 1 : 0.35} />
                <rect x="16" y="5" width="4" height="16" rx="1" fill="currentColor" opacity={tier >= 3 ? 1 : 0.35} />
              </svg>
              <span className="tmua-ws-nav-label">{label}</span>
            </button>
          ))}

          <button
            type="button"
            className={`tmua-ws-nav-item ${page === 'past' ? 'active' : ''}`}
            onClick={() => goTo('past')}
          >
            <svg className="tmua-ws-nav-icon" viewBox="0 0 24 24" fill="none">
              <path d="M4 19.5V6a2 2 0 012-2h10.5a1.5 1.5 0 011.5 1.5V19a1 1 0 01-1 1H6.5A2.5 2.5 0 014 19.5z" stroke="currentColor" strokeWidth="1.6" />
              <line x1="8" y1="8" x2="14" y2="8" stroke="currentColor" strokeWidth="1.6" />
            </svg>
            <span className="tmua-ws-nav-label">Past Papers</span>
          </button>
        </nav>
      </aside>

      <main className="tmua-ws-main">
        <div className="tmua-ws-topbar-mobile">
          <button type="button" className="tmua-ws-icon-btn" onClick={() => setMobileOpen((o) => !o)}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" />
              <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" />
            </svg>
          </button>
          <span>{PAGES[page]}</span>
        </div>

        <div className="tmua-ws-back-row">
          <button type="button" className="tmua-ws-icon-btn tmua-ws-back" title="Back" onClick={onBack}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>

        {page === 'overview' && (
          <section className="tmua-ws-page">
            <div className="tmua-ws-page-header">
              <h1 className="tmua-ws-page-title">Overview</h1>
            </div>

            <div className="tmua-ws-target-card">
              <div className="tmua-ws-target-field tmua-ws-target-field--university">
                <label htmlFor="tmua-track">Target university</label>
                <div className="tmua-ws-target-select-shell">
                  <span className="tmua-ws-target-select-text" aria-hidden="true">
                    {selectDisplay}
                  </span>
                  <select
                    id="tmua-track"
                    className="tmua-ws-target-select"
                    value={selectValue}
                    onChange={onTargetChange}
                  >
                    {TARGET_OPTIONS.map((o) => (
                      <option key={o.label} value={o.label}>
                        {o.label} ({o.value.toFixed(1)}+/9.0)
                      </option>
                    ))}
                    <option value="custom">Custom target…</option>
                  </select>
                </div>
              </div>
              <div className="tmua-ws-target-field">
                <label htmlFor="tmua-custom">Custom score</label>
                <input
                  id="tmua-custom"
                  type="number"
                  className="tmua-ws-target-input"
                  min={1}
                  max={9}
                  step={0.5}
                  placeholder="/ 9.0"
                  hidden={!target.custom}
                  value={target.custom ? target.value : ''}
                  onChange={onCustomInput}
                />
              </div>
              <div className="tmua-ws-target-readout">
                <span className="num">
                  {targetValue.toFixed(1)}
                  <span style={{ color: 'var(--tmua-ink-faint)', fontWeight: 500 }}>/9.0</span>
                </span>
                <div className="lbl">TMUA score target</div>
              </div>
            </div>

            <div className="tmua-ws-section">
              <div className="tmua-ws-progress-status" style={{ marginBottom: 8 }}>
                {avg == null ? (
                  'No scored sittings yet — complete a mock or past paper to see progress.'
                ) : diff >= 0 ? (
                  <>
                    <b>{diff.toFixed(1)} points</b> above target — keep it up
                  </>
                ) : (
                  <>
                    <b>{Math.abs(diff).toFixed(1)} points</b> below target
                  </>
                )}
              </div>
              <div className="tmua-ws-progress-track">
                <div className="tmua-ws-progress-fill" style={{ width: `${fillPct}%` }} />
                <div
                  className="tmua-ws-progress-marker"
                  data-label={markerLabel}
                  style={{ left: `${markerPct}%` }}
                />
              </div>
              <div className="tmua-ws-breakdown-meta">
                Current average:{' '}
                <b style={{ color: 'var(--tmua-ink)', fontFamily: 'var(--tmua-mono)' }}>
                  {formatTmuaGrade(avg)}
                </b>
                {overview.scoredAttempts > 0
                  ? ` · based on ${overview.scoredAttempts} scored sitting${overview.scoredAttempts === 1 ? '' : 's'}`
                  : ''}
              </div>
            </div>

            <div className="tmua-ws-stat-strip">
              <div className="tmua-ws-stat-cell">
                <span className="tmua-ws-stat-value">{overview.totalQuestions.toLocaleString()}</span>
                <span className="tmua-ws-stat-label">Total questions</span>
              </div>
              <div className="tmua-ws-stat-cell">
                <span className="tmua-ws-stat-value">{overview.attempted.toLocaleString()}</span>
                <span className="tmua-ws-stat-label">Questions attempted</span>
              </div>
              <div className="tmua-ws-stat-cell">
                <span className="tmua-ws-stat-value">
                  {overview.successRate != null ? `${overview.successRate}%` : '—'}
                </span>
                <span className="tmua-ws-stat-label">Success rate</span>
              </div>
              <div className="tmua-ws-stat-cell">
                <span className="tmua-ws-stat-value">{formatTmuaGrade(avg)}</span>
                <span className="tmua-ws-stat-label">Average score</span>
              </div>
            </div>

            <div className="tmua-ws-section">
              <h2 className="tmua-ws-section-title">By tier</h2>
              <BreakdownRow
                name="Beginnings"
                meta={`${overview.beginnings.done} of ${overview.beginnings.total} mocks completed`}
                score={formatTmuaGrade(overview.beginnings.avg)}
                pct={overview.beginnings.pct}
                goto="mock-beginnings"
              />
              <BreakdownRow
                name="Foundations"
                meta={`${overview.foundations.done} of ${overview.foundations.total} mocks completed`}
                score={formatTmuaGrade(overview.foundations.avg)}
                pct={overview.foundations.pct}
                goto="mock-foundations"
              />
              <BreakdownRow
                name="Beyond"
                meta={`${overview.beyond.done} of ${overview.beyond.total} mocks completed`}
                score={formatTmuaGrade(overview.beyond.avg)}
                pct={overview.beyond.pct}
                goto="mock-beyond"
              />
            </div>

            <div className="tmua-ws-section">
              <h2 className="tmua-ws-section-title">By type</h2>
              <BreakdownRow
                name="Practice Topics"
                meta={practiceTopicsRow.meta}
                score={practiceTopicsRow.score}
                pct={practiceTopicsRow.pct}
                goto="practice"
              />
              <BreakdownRow
                name="Mock Tests"
                meta={`${overview.mocks.done} of ${overview.mocks.total} mocks completed`}
                score={formatTmuaGrade(overview.mocks.avg)}
                pct={overview.mocks.pct}
                goto="mock-all"
              />
              <BreakdownRow
                name="Past Papers"
                meta={`${overview.past.done} of ${overview.past.total} papers completed`}
                score={formatTmuaGrade(overview.past.avg)}
                pct={overview.past.pct}
                goto="past"
              />
            </div>

            <PracticeTopicsOverview
              prefix="tmua-ws"
              topicsDone={topicsDone}
              topicCount={topicCount}
              papers={sectionProgress}
              onGo={goTo}
            />

            <div className="tmua-ws-section">
              <h2 className="tmua-ws-section-title">By mode</h2>
              <BreakdownRow
                name="Timed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.timed.count} of ${overview.timed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatTmuaGrade(overview.timed.avg)}
                pct={overview.timed.pct}
                staticRow
              />
              <BreakdownRow
                name="Untimed"
                meta={
                  overview.scoredAttempts
                    ? `${overview.untimed.count} of ${overview.untimed.total} attempts`
                    : 'No attempts yet'
                }
                score={formatTmuaGrade(overview.untimed.avg)}
                pct={overview.untimed.pct}
                staticRow
              />
            </div>
          </section>
        )}

        <PracticeTopicsPages
          page={page}
          prefix="tmua-ws"
          exam="TMUA"
          topics={(tier) => (
            topicPapersContent({
              prefix: "tmua-ws",
              tier,
              topicSections,
              topicStatus,
              renderTable: renderTopicsTable,
            })
          )}
          sectionPapers={sectionPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />
        <PracticeTopicsPages
          page={page}
          prefix="tmua-ws"
          exam="TMUA"
          label={SUBTOPIC_PAPERS_LABEL}
          allPage={SUBTOPIC_PAPERS_ALL_PAGE}
          tierPages={SUBTOPIC_TIER_PAGES}
          titles={SUBTOPIC_PAGE_TITLES}
          ofTier={subtopicPapersOfTier}
          papers={subtopicPapers}
          completedList={completedList}
          isPartUnlocked={isPartUnlocked}
          onStartSetPaper={onStartSetPaper}
          onRequestUpgrade={onRequestUpgrade}
        />

        {page === 'mock-all' && (
          <section className="tmua-ws-page">
            <div className="tmua-ws-page-header">
              <h1 className="tmua-ws-page-title">Mock Tests</h1>
            </div>
            {[
              ['Beginnings', beginnings],
              ['Foundations', foundations],
              ['Beyond', beyond],
            ].filter(([, rows]) => rows.length).map(([label, rows]) => (
              <div key={label} className="tmua-ws-section">
                <h2 className="tmua-ws-section-title">{label}</h2>
                {renderTable(rows, 'mock')}
              </div>
            ))}
          </section>
        )}

        {page === 'mock-beginnings' && (
          <section className="tmua-ws-page">
            <div className="tmua-ws-page-header">
              <h1 className="tmua-ws-page-title">Mock Tests: Beginnings</h1>
            </div>
            {renderTable(beginnings, 'mock')}
          </section>
        )}

        {page === 'mock-foundations' && (
          <section className="tmua-ws-page">
            <div className="tmua-ws-page-header">
              <h1 className="tmua-ws-page-title">Mock Tests: Foundations</h1>
            </div>
            {renderTable(foundations, 'mock')}
          </section>
        )}

        {page === 'mock-beyond' && (
          <section className="tmua-ws-page">
            <div className="tmua-ws-page-header">
              <h1 className="tmua-ws-page-title">Mock Tests: Beyond</h1>
            </div>
            {renderTable(beyond, 'mock')}
          </section>
        )}

        {page === 'past' && (
          <section className="tmua-ws-page">
            <div className="tmua-ws-page-header">
              <h1 className="tmua-ws-page-title">Past Papers</h1>
            </div>
            {renderTable(past, 'past')}
          </section>
        )}
      </main>

      <div className={`tmua-ws-toast ${toast ? 'show' : ''}`}>{toast}</div>
    </div>
  );
};

export default TmuaWorkspace;
